import {
  ClipAssetResponse,
  assetsAssetV2List,
  assetsClipBulkDeleteCreate,
  assetsClipBulkDownloadCreate,
  assetsClipCreate,
  assetsClipDestroy,
  assetsClipResetFullLengthClipCreate,
  BulkDeleteResponse,
  BulkDownloadResponse
} from '@goldcast/api/content';
import { Clip } from '@goldcast/player-config/src/types/clip-types';
import {
  ClipDeletes,
  ClipEdits
} from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import { SourceType, CaptionStyle, CaptionLineConfig } from '@goldcast/player-config/src/types/asset-types';
import { Scenes } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Scene';
import {
  DEFAULT_AUDIO_CONTENT_MAGIC_LAYOUT,
  DEFAULT_MAGIC_LAYOUT
} from '@goldcast/player-config/src/constants/clip-defaults';
import { core, isAudioContent as isAudioContentCoreStore } from '@/stores/core';
import { computeCaptionsWithData } from '@/App/remotion/libs/caption/caption';

/** Asset subtypes behind the `clip_types` filter on the asset list API. */
export enum ClipType {
  Clip = 'CLIP',
  FullRecording = 'FULL_RECORDING',
  HighlightReel = 'HIGHLIGHT_REEL'
}

export function isCustomUpload(): boolean {
  const content = core.getSnapshot().content;
  return content?.media_source_type === 'UPLOAD';
}

export function createContentClip(
  start: number,
  end: number,
  source: SourceType,
  deletes: ClipDeletes = {},
  edits: ClipEdits = {},
  scenes?: Scenes,
  fps?: number,
  additionalParams?: Record<string, any>,
  isDuplicate?: boolean,
  title?: string
): Promise<ClipAssetResponse> {
  const content = core.getSnapshot().content;
  const isAudio = isAudioContentCoreStore();
  const defaultLineConfig = CaptionLineConfig.OneLine;

  const startTimeAdjustedWithDeletes =
    Object.values(deletes).find(del => del.bounds[0] < start && del.bounds[1] > start)?.bounds[1] || start;

  const endTimeAdjustedWithDeletes =
    Object.values(deletes).find(del => del.bounds[0] < end && del.bounds[1] > end)?.bounds[0] || end;
  let updatedScenes: Scenes | undefined = undefined;
  if (fps && scenes) {
    const startTimeAdjustedWithDeletesFPS = startTimeAdjustedWithDeletes * fps;
    const endTimeAdjustedWithDeletesFPS = endTimeAdjustedWithDeletes * fps;
    updatedScenes = scenes
      .filter(
        scene => scene.toFrame >= startTimeAdjustedWithDeletesFPS && scene.fromFrame <= endTimeAdjustedWithDeletesFPS
      )
      .map(scene => {
        return {
          ...scene,
          fromFrame: Math.max(scene.fromFrame, startTimeAdjustedWithDeletesFPS),
          toFrame: Math.min(scene.toFrame, endTimeAdjustedWithDeletesFPS)
        };
      });
  }

  const { captions, duration, excludes } = computeCaptionsWithData({
    start: startTimeAdjustedWithDeletes,
    end: endTimeAdjustedWithDeletes,
    edits,
    deletes,
    merges: [],
    isTranslated: false
  });

  return assetsClipCreate({
    body: {
      content_id: content?.id as string,
      organization: content?.organization as string,
      asset_metadata: {
        start: startTimeAdjustedWithDeletes,
        end: endTimeAdjustedWithDeletes,
        size: 'LANDSCAPE',
        layout: isAudio ? 'AUDIOGRAM' : 'DEFAULT',
        source,
        deletes,
        edits,
        scenes: updatedScenes,
        highlight_type: 'text',
        subtitle: true,
        word_highlight_color: isAudio ? '#11184F' : '#330f80',
        magicLayout: isAudio ? DEFAULT_AUDIO_CONTENT_MAGIC_LAYOUT : DEFAULT_MAGIC_LAYOUT,
        is_edited: false,
        hide_borders: true,
        caption_styles: {
          scale: 1,
          animation: CaptionStyle.Outline,
          line_config: defaultLineConfig
        },
        captions,
        duration,
        excludes,
        ...additionalParams
      },
      ...(isDuplicate ? { duplicated_from: content?.id } : {}),
      ...(title ? { title } : {})
    }
  });
}

export function deleteContentClip(clipId: string): Promise<any> {
  return assetsClipDestroy({ id: clipId });
}

export function bulkDeleteContentClips(clipIds: string[]): Promise<BulkDeleteResponse> {
  return assetsClipBulkDeleteCreate({ body: { asset_ids: clipIds } });
}

export function bulkDownloadContentClips(clipIds: string[]): Promise<BulkDownloadResponse> {
  return assetsClipBulkDownloadCreate({
    body: { asset_ids: clipIds, enable_cache: true }
  }) as Promise<BulkDownloadResponse>;
}

export function resetFullLengthClip(clipId: string): Promise<any> {
  return assetsClipResetFullLengthClipCreate({
    id: clipId,
    body: {}
  });
}

/**
 * `types: 'CLIP'` alone still returns full-length recordings, so callers that only want real clips must
 * narrow via `clipTypes`. Omitting it returns every subtype, which the recordings flow relies on.
 */
export function loadClips(
  broadcastId: string,
  options?: { includeDuplicates?: boolean; clipTypes?: ClipType }
): Promise<Clip[]> {
  return assetsAssetV2List({
    queryParams: {
      contents: broadcastId,
      types: 'CLIP',
      limit: 1000,
      ordering: 'rank,-created_at',
      ...(options?.includeDuplicates ? { include_duplicates: true } : {}),
      ...(options?.clipTypes ? { clip_types: options.clipTypes } : {})
    }
  }).then(({ results }) => results as unknown as Clip[]);
}
